export interface PortalProduct {
  sku: string;
  name: string;
  thumb: string;
  price: number;
  stock: number;
  incoming: number;
  eta: string;
  status: 'Published' | 'UnderReview' | 'Discontinued';
  updated: string;
}

export interface PortalOrderLine {
  sku: string;
  name: string;
  qty: number;
  price: number;
}

export type PortalOrderStatus =
  | 'New'
  | 'Confirmed'
  | 'AwaitingPayment'
  | 'Paid'
  | 'ReadyForPickup'
  | 'PickedUp'
  | 'Closed';

export interface PortalOrder {
  po: string;
  date: string;
  status: PortalOrderStatus;
  lines: PortalOrderLine[];
  readyDate?: string;
  documents: { name: string; kind: 'invoice' | 'delivery' }[];
  thread: { id: string; author: 'partner' | 'treejar'; name: string; body: string; when: string }[];
}

export interface PortalReservation {
  no: string;
  spec: string;
  lines: { sku: string; qty: number }[];
  created: string;
  expires: string;
  status: 'Active' | 'Converted' | 'Expired' | 'Declined';
  declineReason?: string;
}

export interface PortalDemandRow {
  model: string;
  requested: number;
  timesAdded: number;
  reserved: number;
  ordered: number;
  missing: number;
}

export interface PortalInvoice {
  invoice: string;
  po: string;
  amount: number;
  status: 'Paid' | 'AwaitingPayment';
  due: string;
}

export interface PortalClaim {
  claim: string;
  po: string;
  sku: string;
  item: string;
  status: 'Open' | 'ReplacementAgreed' | 'Closed';
  updated: string;
  description: string;
  photos: string[];
  thread: { id: string; author: 'partner' | 'treejar'; name: string; body: string; when: string }[];
}

export interface PortalTeamMember {
  name: string;
  email: string;
  role: 'Owner' | 'Warehouse' | 'Sales' | 'Accounting';
  lastLogin: string | null;
  status: 'Active' | 'Invited';
}

export interface PortalPriceItem {
  sku: string;
  name: string;
  price: number;
  stock: number;
  incoming: number;
  eta: string;
  confidence: 'high' | 'medium' | 'low';
}

export const portalPartner = {
  name: 'Al Kanz Office Furniture Tr',
  short: 'Al Kanz',
  warehouse: 'Warehouse No.6, Industrial Area 18',
  city: 'Sharjah',
  user: 'Aisha Al Kanz',
  role: 'Owner',
  email: 'aisha@alkanz.ae',
  discount: 22,
  contractFrom: '2026-11-01',
  contractTo: '2027-10-31',
  manager: 'Layla Hassan',
  managerRole: 'Partner manager · Treejar Trading LLC',
  managerPhone: '+971 50 214 8890',
  managerEmail: 'layla@treejartrading.ae',
  published: 148,
  totalSkus: 156,
  stockUnits: 12896,
  reservedUnits: 420,
  ordersMonth: 6,
  ordersMonthValue: 84300,
  awaitingAmount: 31200,
};

export const portalProducts: PortalProduct[] = [
  {
    sku: 'CH-229A',
    name: 'Ergo Mesh Task Chair',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20black%20mesh%20ergonomic%20task%20chair%20with%20polished%20aluminium%20base%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-ch229a&orientation=squarish',
    price: 455,
    stock: 0,
    incoming: 120,
    eta: '2026-11-14',
    status: 'Published',
    updated: '2026-10-15',
  },
  {
    sku: 'EVL-004B',
    name: 'Evolve Operator Chair, Black',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20black%20fabric%20operator%20office%20chair%20with%20black%20nylon%20base%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-evl004b&orientation=squarish',
    price: 295,
    stock: 240,
    incoming: 0,
    eta: '',
    status: 'Published',
    updated: '2026-10-22',
  },
  {
    sku: 'HY-128C',
    name: 'Hybrid Meeting Table 2400',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20long%20light%20oak%20meeting%20table%20with%20slim%20black%20metal%20legs%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-hy128c&orientation=squarish',
    price: 205,
    stock: 16,
    incoming: 80,
    eta: '2026-11-02',
    status: 'Published',
    updated: '2026-10-22',
  },
  {
    sku: 'CH-601A',
    name: 'Contour High-Back Executive Chair',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20high%20back%20executive%20chair%20in%20dark%20brown%20leather%20with%20chrome%20base%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-ch601a&orientation=squarish',
    price: 520,
    stock: 300,
    incoming: 0,
    eta: '',
    status: 'Published',
    updated: '2026-10-23',
  },
  {
    sku: 'ML-120M',
    name: 'Malla Lounge Chair, Sand',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20sand%20beige%20upholstered%20lounge%20armchair%20with%20wooden%20legs%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-ml120m&orientation=squarish',
    price: 470,
    stock: 180,
    incoming: 40,
    eta: '2026-11-21',
    status: 'Published',
    updated: '2026-10-21',
  },
  {
    sku: 'WS-240L',
    name: 'Linear Workstation 1600, Left Return',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20grey%20office%20workstation%20desk%20with%20white%20laminate%20top%20and%20left%20return%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-ws240l&orientation=squarish',
    price: 890,
    stock: 64,
    incoming: 24,
    eta: '2026-12-01',
    status: 'Published',
    updated: '2026-10-20',
  },
  {
    sku: 'ST-180D',
    name: 'Summit Executive Desk 1800',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20large%20walnut%20executive%20desk%20with%20leather%20inlay%20top%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-st180d&orientation=squarish',
    price: 1250,
    stock: 32,
    incoming: 0,
    eta: '',
    status: 'Published',
    updated: '2026-10-15',
  },
  {
    sku: 'CR-160S',
    name: 'Crate Low Cabinet 1600',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20low%20grey%20office%20storage%20cabinet%20with%20four%20drawers%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-cr160s&orientation=squarish',
    price: 610,
    stock: 88,
    incoming: 0,
    eta: '',
    status: 'Discontinued',
    updated: '2026-09-28',
  },
  {
    sku: 'MT-320O',
    name: 'Orbit Boardroom Table 3200',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20large%20oval%20boardroom%20table%20in%20dark%20oak%20with%20brushed%20brass%20base%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-mt320o&orientation=squarish',
    price: 2100,
    stock: 12,
    incoming: 6,
    eta: '2026-11-28',
    status: 'Published',
    updated: '2026-10-19',
  },
  {
    sku: 'PF-090A',
    name: 'Acoustic Screen 900, Ash',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20light%20grey%20acoustic%20desk%20screen%20panel%20with%20fabric%20surface%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-pf090a&orientation=squarish',
    price: 240,
    stock: 410,
    incoming: 0,
    eta: '',
    status: 'Published',
    updated: '2026-10-23',
  },
  {
    sku: 'PD-045W',
    name: 'Wander Mobile Pedestal',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20white%20mobile%20pedestal%20with%20three%20drawers%20and%20castors%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-pd045w&orientation=squarish',
    price: 310,
    stock: 156,
    incoming: 60,
    eta: '2026-11-09',
    status: 'Published',
    updated: '2026-10-18',
  },
  {
    sku: 'SF-210T',
    name: 'Tess Sofa 3-Seater, Graphite',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20graphite%20grey%20three%20seater%20sofa%20with%20clean%20modern%20lines%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-sf210t&orientation=squarish',
    price: 1680,
    stock: 24,
    incoming: 0,
    eta: '',
    status: 'UnderReview',
    updated: '2026-10-12',
  },
  {
    sku: 'CH-314K',
    name: 'Kite Visitor Chair, Ochre',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20an%20ochre%20mustard%20fabric%20visitor%20chair%20with%20slim%20metal%20frame%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-ch314k&orientation=squarish',
    price: 375,
    stock: 200,
    incoming: 0,
    eta: '',
    status: 'Published',
    updated: '2026-10-22',
  },
  {
    sku: 'DS-140R',
    name: 'Ridge Desk 1400 with Cable Tray',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20light%20ash%20wood%20office%20desk%20with%20black%20steel%20frame%20and%20cable%20tray%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-ds140r&orientation=squarish',
    price: 720,
    stock: 58,
    incoming: 18,
    eta: '2026-11-16',
    status: 'Published',
    updated: '2026-10-16',
  },
  {
    sku: 'RC-280F',
    name: 'Facet Reception Counter',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20white%20and%20oak%20reception%20counter%20with%20faceted%20front%20panel%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-rc280f&orientation=squarish',
    price: 2450,
    stock: 6,
    incoming: 4,
    eta: '2026-12-05',
    status: 'Published',
    updated: '2026-10-11',
  },
  {
    sku: 'CT-090G',
    name: 'Grove Coffee Table',
    thumb:
      'https://readdy.ai/api/search-image?query=Minimal%20studio%20product%20photo%20of%20a%20round%20oak%20coffee%20table%20with%20soft%20rounded%20edges%2C%20soft%20neutral%20grey%20seamless%20background%2C%20even%20diffused%20studio%20lighting%2C%20clean%20commercial%20catalog%20style&width=240&height=240&seq=portal-ct090g&orientation=squarish',
    price: 380,
    stock: 47,
    incoming: 0,
    eta: '',
    status: 'Published',
    updated: '2026-10-17',
  },
];

const productName = (sku: string) => portalProducts.find((p) => p.sku === sku)?.name || sku;

export const portalOrders: PortalOrder[] = [
  {
    po: 'PO-2026-0142',
    date: '2026-10-21',
    status: 'AwaitingPayment',
    lines: [
      { sku: 'CH-229A', name: productName('CH-229A'), qty: 40, price: 455 },
      { sku: 'EVL-004B', name: productName('EVL-004B'), qty: 25, price: 295 },
    ],
    documents: [{ name: 'Invoice-2026-0394.pdf', kind: 'invoice' }],
    thread: [
      {
        id: 'm1',
        author: 'treejar',
        name: 'Layla Hassan',
        body: 'Order confirmed and prepared for pickup. Invoice sent to your accounting email.',
        when: '2026-10-22 09:14',
      },
      {
        id: 'm2',
        author: 'partner',
        name: 'Aisha Al Kanz',
        body: 'Thank you. Stock is ready at the warehouse, gate 2.',
        when: '2026-10-22 11:02',
      },
    ],
  },
  {
    po: 'PO-2026-0138',
    date: '2026-10-17',
    status: 'ReadyForPickup',
    readyDate: '2026-10-24',
    lines: [{ sku: 'HY-128C', name: productName('HY-128C'), qty: 120, price: 205 }],
    documents: [{ name: 'Invoice-2026-0395.pdf', kind: 'invoice' }],
    thread: [
      {
        id: 'm3',
        author: 'treejar',
        name: 'Layla Hassan',
        body: 'Pickup scheduled for Saturday morning. Please have the pallets wrapped.',
        when: '2026-10-23 15:40',
      },
    ],
  },
  {
    po: 'PO-2026-0145',
    date: '2026-10-24',
    status: 'New',
    lines: [{ sku: 'PD-045W', name: productName('PD-045W'), qty: 50, price: 310 }],
    documents: [],
    thread: [],
  },
  {
    po: 'PO-2026-0146',
    date: '2026-10-24',
    status: 'New',
    lines: [{ sku: 'PF-090A', name: productName('PF-090A'), qty: 120, price: 240 }],
    documents: [],
    thread: [],
  },
  {
    po: 'PO-2026-0135',
    date: '2026-10-14',
    status: 'Paid',
    lines: [
      { sku: 'CH-601A', name: productName('CH-601A'), qty: 30, price: 520 },
      { sku: 'ML-120M', name: productName('ML-120M'), qty: 20, price: 470 },
    ],
    documents: [
      { name: 'Invoice-2026-0391.pdf', kind: 'invoice' },
      { name: 'Delivery-note-0135.pdf', kind: 'delivery' },
    ],
    thread: [],
  },
  {
    po: 'PO-2026-0129',
    date: '2026-10-02',
    status: 'PickedUp',
    lines: [{ sku: 'WS-240L', name: productName('WS-240L'), qty: 12, price: 890 }],
    documents: [{ name: 'Delivery-note-0129.pdf', kind: 'delivery' }],
    thread: [],
  },
  {
    po: 'PO-2026-0118',
    date: '2026-09-18',
    status: 'Closed',
    lines: [
      { sku: 'SF-210T', name: productName('SF-210T'), qty: 8, price: 1680 },
      { sku: 'CT-090G', name: productName('CT-090G'), qty: 10, price: 380 },
    ],
    documents: [{ name: 'Invoice-2026-0384.pdf', kind: 'invoice' }],
    thread: [],
  },
];

export const portalReservations: PortalReservation[] = [
  {
    no: 'R-0311',
    spec: 'TS-SPEC-0918',
    lines: [
      { sku: 'CH-601A', qty: 30 },
      { sku: 'PD-045W', qty: 15 },
    ],
    created: '2026-10-22',
    expires: '2026-10-26',
    status: 'Active',
  },
  {
    no: 'R-0309',
    spec: 'TS-SPEC-0915',
    lines: [{ sku: 'CT-090G', qty: 25 }],
    created: '2026-10-21',
    expires: '2026-10-26',
    status: 'Active',
  },
  {
    no: 'R-0307',
    spec: 'TS-SPEC-0912',
    lines: [{ sku: 'ML-120M', qty: 60 }],
    created: '2026-10-21',
    expires: '2026-10-29',
    status: 'Active',
  },
  {
    no: 'R-0305',
    spec: 'TS-SPEC-0908',
    lines: [{ sku: 'EVL-004B', qty: 45 }],
    created: '2026-10-19',
    expires: '2026-10-25',
    status: 'Active',
  },
  {
    no: 'R-0303',
    spec: 'TS-SPEC-0904',
    lines: [{ sku: 'HY-128C', qty: 40 }],
    created: '2026-10-12',
    expires: '2026-10-19',
    status: 'Converted',
  },
  {
    no: 'R-0301',
    spec: 'TS-SPEC-0899',
    lines: [{ sku: 'CH-229A', qty: 20 }],
    created: '2026-10-10',
    expires: '2026-10-17',
    status: 'Expired',
  },
  {
    no: 'R-0298',
    spec: 'TS-SPEC-0895',
    lines: [{ sku: 'PF-090A', qty: 60 }],
    created: '2026-10-06',
    expires: '2026-10-13',
    status: 'Declined',
    declineReason: 'Stock reserved for a confirmed order',
  },
];

export const portalDemand: PortalDemandRow[] = [
  { model: 'CH-229A', requested: 312, timesAdded: 18, reserved: 96, ordered: 120, missing: 24 },
  { model: 'EVL-004B', requested: 268, timesAdded: 14, reserved: 110, ordered: 80, missing: 0 },
  { model: 'HY-128C', requested: 240, timesAdded: 12, reserved: 88, ordered: 120, missing: 0 },
  { model: 'CH-601A', requested: 196, timesAdded: 11, reserved: 90, ordered: 60, missing: 18 },
  { model: 'ML-120M', requested: 174, timesAdded: 9, reserved: 80, ordered: 60, missing: 0 },
  { model: 'WS-240L', requested: 128, timesAdded: 8, reserved: 44, ordered: 12, missing: 6 },
  { model: 'PF-090A', requested: 96, timesAdded: 7, reserved: 40, ordered: 60, missing: 0 },
  { model: 'SF-210T', requested: 64, timesAdded: 5, reserved: 24, ordered: 8, missing: 0 },
  { model: 'ST-180D', requested: 52, timesAdded: 4, reserved: 12, ordered: 0, missing: 9 },
  { model: 'CT-090G', requested: 38, timesAdded: 3, reserved: 25, ordered: 10, missing: 0 },
];

export const portalInvoices: PortalInvoice[] = [
  { invoice: 'INV-2026-0391', po: 'PO-2026-0135', amount: 25000, status: 'Paid', due: '2026-10-20' },
  { invoice: 'INV-2026-0388', po: 'PO-2026-0129', amount: 10680, status: 'Paid', due: '2026-10-10' },
  { invoice: 'INV-2026-0394', po: 'PO-2026-0142', amount: 25575, status: 'AwaitingPayment', due: '2026-11-06' },
  { invoice: 'INV-2026-0396', po: 'PO-2026-0144', amount: 5625, status: 'AwaitingPayment', due: '2026-11-02' },
];

export const portalClaims: PortalClaim[] = [
  {
    claim: 'WC-0091',
    po: 'PO-2026-0118',
    sku: 'CH-229A',
    item: productName('CH-229A'),
    status: 'Open',
    updated: '2026-10-22',
    description:
      'Gas lift is failing after four months of use. The chair sinks during the day and the client reported it twice.',
    photos: [
      'https://readdy.ai/api/search-image?query=Close%20up%20photo%20of%20a%20black%20office%20chair%20gas%20lift%20mechanism%20under%20the%20seat%2C%20warehouse%20floor%20background%2C%20documentary%20inspection%20photo%2C%20natural%20light&width=640&height=480&seq=portal-claim-1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Photo%20of%20an%20office%20chair%20seat%20base%20with%20damaged%20plastic%20cover%20on%20a%20grey%20warehouse%20floor%2C%20documentary%20inspection%20photo%2C%20natural%20light&width=640&height=480&seq=portal-claim-2&orientation=landscape',
    ],
    thread: [
      {
        id: 'c1',
        author: 'treejar',
        name: 'Layla Hassan',
        body: 'Client in Business Bay reports the chair sinking. Photos attached. Please confirm whether you can replace the gas lift or the full chair.',
        when: '2026-10-22 10:20',
      },
    ],
  },
  {
    claim: 'WC-0088',
    po: 'PO-2026-0135',
    sku: 'ML-120M',
    item: productName('ML-120M'),
    status: 'ReplacementAgreed',
    updated: '2026-10-19',
    description: 'Fabric seam opened on the left armrest on delivery. Replacement unit agreed, waiting for the next pickup.',
    photos: [
      'https://readdy.ai/api/search-image?query=Close%20up%20photo%20of%20a%20beige%20upholstered%20armchair%20armrest%20with%20an%20open%20fabric%20seam%2C%20neutral%20showroom%20background%2C%20documentary%20inspection%20photo&width=640&height=480&seq=portal-claim-3&orientation=landscape',
    ],
    thread: [
      {
        id: 'c2',
        author: 'treejar',
        name: 'Layla Hassan',
        body: 'Replacement agreed with the client. We will collect the faulty unit together with the next pickup.',
        when: '2026-10-19 14:05',
      },
    ],
  },
  {
    claim: 'WC-0084',
    po: 'PO-2026-0129',
    sku: 'WS-240L',
    item: productName('WS-240L'),
    status: 'Closed',
    updated: '2026-10-06',
    description: 'Missing cable tray bracket in two workstations. Brackets delivered separately and installed.',
    photos: [],
    thread: [
      {
        id: 'c3',
        author: 'partner',
        name: 'Rashid Haddad',
        body: 'Two brackets sent with the courier, delivered on 6 October.',
        when: '2026-10-06 09:10',
      },
    ],
  },
];

export const portalTeam: PortalTeamMember[] = [
  {
    name: 'Aisha Al Kanz',
    email: 'aisha@alkanz.ae',
    role: 'Owner',
    lastLogin: '2026-10-24 08:12',
    status: 'Active',
  },
  {
    name: 'Rashid Haddad',
    email: 'rashid@alkanz.ae',
    role: 'Warehouse',
    lastLogin: '2026-10-24 07:40',
    status: 'Active',
  },
  {
    name: 'Priya Nair',
    email: 'priya@alkanz.ae',
    role: 'Sales',
    lastLogin: '2026-10-23 17:05',
    status: 'Active',
  },
  {
    name: 'Mohammed Iqbal',
    email: 'accounts@alkanz.ae',
    role: 'Accounting',
    lastLogin: '2026-10-22 11:20',
    status: 'Active',
  },
  {
    name: 'Fatima Saeed',
    email: 'fatima@alkanz.ae',
    role: 'Warehouse',
    lastLogin: null,
    status: 'Invited',
  },
];

export const portalUploadItems: PortalPriceItem[] = [
  { sku: 'CH-229A', name: productName('CH-229A'), price: 455, stock: 0, incoming: 120, eta: '2026-11-14', confidence: 'high' },
  { sku: 'EVL-004B', name: productName('EVL-004B'), price: 295, stock: 240, incoming: 0, eta: '', confidence: 'high' },
  { sku: 'HY-128C', name: productName('HY-128C'), price: 205, stock: 16, incoming: 80, eta: '2026-11-02', confidence: 'high' },
  { sku: 'CH-601A', name: productName('CH-601A'), price: 520, stock: 300, incoming: 0, eta: '', confidence: 'medium' },
  { sku: 'ML-120M', name: productName('ML-120M'), price: 470, stock: 180, incoming: 40, eta: '2026-11-21', confidence: 'high' },
  { sku: 'WS-240L', name: productName('WS-240L'), price: 890, stock: 64, incoming: 24, eta: '2026-12-01', confidence: 'low' },
  { sku: 'ST-180D', name: productName('ST-180D'), price: 1250, stock: 32, incoming: 0, eta: '', confidence: 'medium' },
  { sku: 'PF-090A', name: productName('PF-090A'), price: 240, stock: 410, incoming: 0, eta: '', confidence: 'high' },
  { sku: 'PD-045W', name: productName('PD-045W'), price: 310, stock: 156, incoming: 60, eta: '2026-11-09', confidence: 'low' },
  { sku: 'DS-140R', name: productName('DS-140R'), price: 720, stock: 58, incoming: 18, eta: '2026-11-16', confidence: 'high' },
];

export const portalUploadHistory = [
  { version: 'v12', date: '2026-10-22', items: 148, status: 'Active', changes: '+3 / −1 / 7' },
  { version: 'v11', date: '2026-10-07', items: 141, status: 'Superseded', changes: '+5 / −2 / 12' },
  { version: 'v10', date: '2026-09-19', items: 138, status: 'Superseded', changes: '+2 / −0 / 4' },
  { version: 'v9', date: '2026-08-30', items: 136, status: 'Superseded', changes: '+8 / −3 / 19' },
];

export const portalDiff = [
  { type: 'new', sku: 'CT-090G', name: productName('CT-090G'), was: '—', now: 'AED 380 · 47 units' },
  { type: 'new', sku: 'RC-280F', name: productName('RC-280F'), was: '—', now: 'AED 2,450 · 6 units' },
  { type: 'removed', sku: 'CR-160S', name: productName('CR-160S'), was: 'AED 610 · 88 units', now: '—' },
  { type: 'price', sku: 'CH-229A', name: productName('CH-229A'), was: 'AED 442', now: 'AED 455' },
  { type: 'price', sku: 'ML-120M', name: productName('ML-120M'), was: 'AED 465', now: 'AED 470' },
  { type: 'stock', sku: 'CH-601A', name: productName('CH-601A'), was: '240 units', now: '300 units' },
  { type: 'stock', sku: 'PF-090A', name: productName('PF-090A'), was: '360 units', now: '410 units' },
  { type: 'stock', sku: 'HY-128C', name: productName('HY-128C'), was: '40 units', now: '16 units' },
];

export const portalEvents = [
  { id: 'e1', type: 'order', po: 'PO-2026-0146', units: 120, when: '2026-10-24 09:12' },
  { id: 'e2', type: 'payment', invoice: 'INV-2026-0391', amount: 'AED 25,000', when: '2026-10-23 16:40' },
  { id: 'e3', type: 'reservation', no: 'R-0301', when: '2026-10-22 18:00' },
  { id: 'e4', type: 'stock', sku: 'CH-601A', when: '2026-10-21 08:25' },
  { id: 'e5', type: 'ready', po: 'PO-2026-0138', when: '2026-10-20 12:15' },
  { id: 'e6', type: 'warranty', no: 'WC-0091', sku: 'CH-229A', when: '2026-10-22 10:20' },
];

export const portalNotificationDefaults = [
  { key: 'evOrders', email: true, whatsapp: true },
  { key: 'evPayments', email: true, whatsapp: false },
  { key: 'evReservations', email: true, whatsapp: true },
  { key: 'evStock', email: true, whatsapp: false },
  { key: 'evWarranty', email: true, whatsapp: true },
  { key: 'evPriceList', email: false, whatsapp: false },
];